import type { JSX, ReactNode } from 'react';
import { Children } from 'react';
import { cn } from '@/lib/utils';
import { Icon, type IconName } from '@/components/ui/icon';

export interface ActivityTileProps {
  /** Context title - what/where (e.g., plant name, report code) */
  title: string;
  /** Secondary context - when/status (e.g., date, last updated) */
  subtitle?: string;
  /** Optional chips/badges in top area */
  meta?: ReactNode;
  /** Verb shown in command bar (e.g., "Modifica", "Continua") */
  actionLabel: string;
  /** Icon shown in command bar */
  icon: IconName;
  /** Whether the action is overdue - shows a subtle warning indicator */
  overdue?: boolean;
  /** Click handler for the entire tile */
  onClick: () => void;
  /** Additional className */
  className?: string;
}

export function ActivityTile({
  title,
  subtitle,
  // meta is available in props but not currently rendered
  meta: _meta,
  actionLabel,
  icon,
  overdue = false,
  onClick,
  className,
}: ActivityTileProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative flex w-full items-center gap-4 rounded-lg border bg-white p-3 pr-4 text-left shadow-sm transition-all duration-200',
        'border-neutral-200 hover:border-brand-blue-300 hover:shadow-md active:scale-[0.99]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-500/30',
        className
      )}
    >
      {/* Accent Strip - Orange by default, red for overdue */}
      <div
        className={cn(
          'absolute bottom-1 left-1 top-1 w-1 rounded-full transition-colors',
          overdue ? 'bg-error-500' : 'bg-brand-accent'
        )}
      />

      {/* Icon Area */}
      <div className="ml-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-neutral-50 text-neutral-500 transition-colors group-hover:bg-brand-blue-50 group-hover:text-brand-blue-600">
        <Icon name={icon} className="h-5 w-5" />
      </div>

      {/* Content Stack */}
      <div className="flex flex-1 flex-col justify-center min-w-0">
        <div className="flex items-center gap-2">
          {title ? (
            <span className="truncate text-[10px] font-bold uppercase tracking-widest text-text-secondary opacity-80">
              {title}
            </span>
          ) : null}
          {subtitle ? (
            <>
              <span className="text-[10px] text-neutral-300">•</span>
              <span className="truncate text-[10px] text-text-tertiary">{subtitle}</span>
            </>
          ) : null}
        </div>

        <span className="truncate text-base font-semibold leading-tight text-text-primary transition-colors group-hover:text-brand-blue-700">
          {actionLabel}
        </span>
      </div>

      {/* Action Trigger (Play) */}
      <div className="flex shrink-0 items-center gap-2 pl-3 transition-colors text-brand-accent">
        <div className="h-5 w-[1px] bg-neutral-100" />
        <Icon
          name="play_circle"
          className="h-6 w-6 transition-transform group-hover:scale-110 fill-brand-accent/5"
        />
      </div>
    </button>
  );
}

interface ActivityBarProps {
  /** Section title (optional, uppercase label) */
  title?: string;
  /** Header-level actions (right-aligned) */
  actions?: ReactNode;
  /** ActivityTile children */
  children?: ReactNode;
  /** Message shown when no tiles */
  emptyMessage?: string;
  /** Additional className */
  className?: string;
}

export function ActivityBar({
  title,
  actions,
  children,
  emptyMessage = 'Nessuna attività prioritaria',
  className,
}: ActivityBarProps): JSX.Element {
  const tiles = Children.toArray(children);
  const hasTiles = tiles.length > 0;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {title || actions ? (
        <div className="flex items-center justify-between gap-4">
          {title ? (
            <h3 className="text-sm font-semibold uppercase tracking-wide text-text-secondary">
              {title}
            </h3>
          ) : null}
          {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}

      {hasTiles ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">{tiles}</div>
      ) : (
        <p className="text-sm italic text-text-secondary">{emptyMessage}</p>
      )}
    </div>
  );
}

// Backward compatibility aliases
/** @deprecated Use ActivityTileProps instead */
export type ActionTileProps = ActivityTileProps;
/** @deprecated Use ActivityTile instead */
export const ActionTile = ActivityTile;
/** @deprecated Use ActivityBar instead */
export const ActionBar = ActivityBar;
